<!-- 修改 template 部分的最外层结构 -->
<template>
  <!-- Loading 遮罩保持不变 -->
  <div v-if="isLoading" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center no-print">
    <div class="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
  </div>

  <!-- 下载 PDF 按钮 -->
  <div v-if="canResearchExport && (!serverPdf)" class="fixed top-4 right-4 z-50 no-print">
    <button
      @click="downloadPDF"
      :disabled="!isDataFullyLoaded || isDownloading"
      class="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
    >
      <svg v-if="!isDownloading" class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
      <svg v-else class="w-5 h-5 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      <span>{{ isDownloading ? t('generating') : t('download_pdf') }}</span>
    </button>
  </div>

  <div v-if="profileError" class="no-print m-4 rounded-lg bg-red-50 p-4 text-red-600" role="alert">{{ profileError }} <button class="underline" @click="fetchReportInfo">{{ t('cohort_profile_retry') }}</button></div>

  <!-- 报告页面容器 -->
  <div class="report-container" id="report-content">
    <!-- 第一页 -->
    <div class="report-page">
      <!-- 页眉 -->
      <header class="page-header">
        <div class="flex justify-between items-center px-4 pb-3">
          <h1 class="text-base font-medium text-gray-900">{{ t('sleep_analysis_report') }}</h1>
          <span class="text-black/80 text-sm">{{ formatChineseDate(selectedDate) }}</span>
        </div>
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </header>

      <!-- 第一页内容 -->
      <div class="page-content space-y-4">
        <!-- 基本信息 Card -->
        <div class="content-section">
          <!-- User Info Card -->
        <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
            <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
            {{ t('basic_info') }}
          </h2>
          
          <div class="grid grid-cols-4 lg:grid-cols-8 gap-1.5">
            <!-- 基础信息 -->
            <template v-for="(item, index) in baseInfoItems" :key="'base-' + index">
              <div class="bg-gray-50 rounded-lg p-1.5 hover:bg-gray-100/80 transition-all duration-300">
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-500">{{ item.label }}</span>
                  <span class="text-xs font-medium text-gray-900">{{ item.value }}</span>
                </div>
              </div>
            </template>

            <!-- 自定义字段 -->
            <template v-for="(field, key) in customFields" :key="'custom-' + key">
              <div class="bg-gray-50 rounded-lg p-1.5 hover:bg-gray-100/80 transition-all duration-300">
                <div class="flex justify-between items-center">
                  <span class="text-xs text-gray-500">{{ key }}</span>
                  <span class="text-xs font-medium text-gray-900">{{ field.value }} {{ field.unit }}</span>
                </div>
              </div>
            </template>
          </div>
        </div>
  
        </div>

        <!-- 睡眠分析 Card -->
        <div class="content-section">
  <!-- Sleep Analysis -->
        <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
            <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
            {{ t('sleep_analysis') }}
          </h2>
          
          <div class="space-y-1.5">
            <!-- 第一行：睡眠时间 -->
            <div class="bg-gray-50 rounded-lg p-2">
              <h3 class="text-xs font-medium text-gray-700 mb-1">{{ t('sleep_time_t') }}</h3>
              <div class="grid grid-cols-4 gap-2">
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('lights_off_time') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ formatTime(data.sleep_intention_time) }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('lights_on_time') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ formatTime(data.intention_leave_time) }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('total_sleep_duration') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[2].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_efficiency_t') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[3].value }}</span>
                </div>
              </div>
            </div>

            <!-- 第二行：睡眠质量 -->
            <div class="bg-gray-50 rounded-lg p-2">
              <h3 class="text-xs font-medium text-gray-700 mb-1">{{ t('sleep_quality_t') }}</h3>
              <div class="grid grid-cols-4 gap-2">
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_onset_speed') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[4].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_stability') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[5].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('deep_sleep_t') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[6].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('rem_latency') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[7].value }}</span>
                </div>
              </div>
            </div>

            <!-- 第三行：睡眠评分 -->
            <div class="bg-gray-50 rounded-lg p-2">
              <h3 class="text-xs font-medium text-gray-700 mb-1">{{ t('sleep_score_t') }}</h3>
              <div class="grid grid-cols-4 gap-2">
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_interruption') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[5].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_fragmentation') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[7].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_regularity') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[8].value }}</span>
                </div>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('sleep_quality_t') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{ computedSleepAnalysis[9].value }}</span>
                </div>
              </div>
            </div>
            <!-- 第三行：量表分数 -->
            <div v-if="essScore !== null" class="bg-gray-50 rounded-lg p-2">
              <h3 class="text-xs font-medium text-gray-700 mb-1">{{ t('sleep_score_t') }}</h3>
              <div class="grid grid-cols-4 gap-2">

                <div  v-if="essScore !== null"  class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('ess_score') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{essScore}}</span>
                </div>
                <div  v-if="psqiScore !== null"  class="flex flex-col">
                  <span class="text-xs text-gray-600">{{ t('psqi_score') }}</span>
                  <span class="text-sm font-bold text-blue-600">{{psqiScore}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>

        <!-- 睡眠结构 Card -->
        <div class="content-section">
 <!-- Sleep Structure -->
 <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
  <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
    <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
    {{ t('sleep_structure') }}
  </h2>

  <div class="flex flex-col md:flex-row gap-2 print-sleep-structure">
    <!-- 左侧睡眠阶段统计 - PDF中显示为2x2网格 -->
    <div class="w-full md:w-1/4 print-sleep-phases">
      <div class="grid grid-cols-1 md:grid-cols-2 print-grid-2x2 gap-1.5">
        <div v-for="(phase, index) in computedSleepPhases" 
             :key="index"
             class="bg-gray-50 rounded-lg p-2.5"
             :class="{
               'border-l-4 border-l-orange-400': index === 0,
               'border-l-4 border-l-blue-300': index === 1,
               'border-l-4 border-l-blue-500': index === 2,
               'border-l-4 border-l-blue-700': index === 3
             }"
        >
          <!-- 标题行 -->
          <div class="text-sm font-medium text-gray-700 mb-1">
            {{ phase.label }}
          </div>
          
          <!-- 数据行 -->
          <div class="flex items-center justify-between">
            <!-- 时长 -->
            <div class="flex items-baseline">
              <span class="text-base font-semibold text-gray-900">{{ phase.value }}</span>
            </div>
            <!-- 百分比 -->
            <span class="text-sm font-medium text-blue-600">{{ phase.percentage }}</span>
          </div>
        </div>
      </div>
    </div>

  <!-- 右侧图表区域 -->
  <div class="w-full md:w-3/4 flex flex-col gap-2">
    <!-- 睡眠结构图表 -->
    <div class="flex-1 bg-white rounded-lg border border-gray-100">
        <div class="h-[140px] relative print-chart-container">
          <div :id="chartId" class="w-full h-full" style="min-height: 140px;"></div>
          <div v-if="!data" class="absolute inset-0 flex items-center justify-center bg-gray-50/80 no-print">
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    </div>

      <!-- 体动图表 -->
    <div class="flex-1 bg-white rounded-lg border border-gray-100">
        <div class="h-[140px] relative print-chart-container">
          <div :id="ArtChartId" class="w-full h-full" style="min-height: 140px;"></div>
          <div v-if="!data" class="absolute inset-0 flex items-center justify-center bg-gray-50/80 no-print">
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </div>
    </div>
  </div>
  </div>
</div>
        </div>
      </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-3"></div>
        <span class="text-gray-500">{{ t('page_1') }}</span>
      </footer>
    </div>

    <!-- 第二页 - 如果第一页内容不足以填满，这里会紧接第一页 -->
    <div class="report-page">
      <!-- 页眉 -->
      <header class="page-header">
        <div class="flex justify-between items-center px-4 pb-3">
          <h1 class="text-base font-medium text-gray-900">{{ t('sleep_analysis_report') }}</h1>
          <span class="text-black/80 text-sm">{{ formatChineseDate(selectedDate) }}</span>
        </div>
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </header>

      <!-- 第二页内容 -->
      <div class="page-content space-y-4">
        <!-- 呼吸健康 Card -->
        <div class="content-section">
  <!-- Breathing Health -->
  <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
    <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
      <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
      {{ t('respiratory_health') }}
    </h2>
    
    <div class="flex flex-col md:flex-row gap-2">
      <!-- 左侧统计信息 -->
      <div class="w-full md:w-1/4">
        <div class="grid grid-cols-1 gap-2">
          <div v-for="(item, index) in computedBreathingHealth" 
               :key="index"
               class="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-all"
          >
            <span class="text-sm text-gray-500">{{ item.label }}</span>
            <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
          </div>
        </div>
      </div>

      <!-- 右侧图表区域 -->
      <div class="w-full md:w-3/4 space-y-2">
        <!-- 呼吸暂停事件图表 -->
        <div class="bg-white rounded-lg border border-gray-100">
          <h3 class="text-sm font-medium px-3 pt-2 text-gray-500">{{ isOAHIDevice ? t('whole_night_oahi_events_full') : t('whole_night_ahi_events_full') }}</h3>
          <div class="h-[140px]">
            <div :id="AhChartId" class="w-full h-full"></div>
          </div>
        </div>

        <!-- 鼾声分析图表 -->
        <div class="bg-white rounded-lg border border-gray-100">
          <h3 class="text-sm font-medium px-3 pt-2 text-gray-500">{{ t('snore_analysis') }}</h3>
          <div class="h-[140px]">
            <div :id="SnoreChartId" class="w-full h-full"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
    </div>

        <!-- 睡眠期生理指标 Card -->
        <div class="content-section">
 <!-- Sleep Period Indicators & Charts -->
 <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
  <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
    <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
    {{ t('sleep_physiology_metrics') }}
  </h2>
  
  <div class="space-y-4">
    <!-- 指标卡片 -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-2">
      <div v-for="(item, index) in computedSleepPeriodIndicators" 
           :key="index"
           class="flex flex-col p-2.5 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-center"
      >
        <span class="text-sm text-gray-500 mb-1">{{ item.label }}</span>
        <div class="flex items-center justify-center gap-1">
          <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
          <span v-if="item.trend" 
                class="text-xs"
                :class="{
                  'text-green-500': item.trend > 0,
                  'text-red-500': item.trend < 0,
                  'text-gray-400': item.trend === 0
                }"
          >
            {{ item.trend > 0 ? '↑' : item.trend < 0 ? '↓' : '–' }}
          </span>
        </div>
      </div>
    </div>

    <!-- 图表区域 - 打印时强制2列布局 -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 print-physiological-charts">
      <!-- 睡眠心率变异性 -->
      <div class="bg-white rounded-lg border border-gray-100">
        <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_hrv') }}</h3>
        <div class="h-[140px] p-2">
          <div :id="HrvChartId" class="w-full h-full"></div>
        </div>
      </div>

      <!-- 睡眠呼吸频率 -->
      <div class="bg-white rounded-lg border border-gray-100">
        <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_respiratory_rate') }}</h3>
        <div class="h-[140px] p-2">
          <div :id="RrChartId" class="w-full h-full"></div>
        </div>
      </div>

      <!-- 睡眠心率 -->
      <div class="bg-white rounded-lg border border-gray-100">
        <h3 class="text-sm font-medium px-3 py-2 border-b border-gray-100">{{ t('sleep_heart_rate') }}</h3>
        <div class="h-[140px] p-2">
          <div :id="HrChartId" class="w-full h-full"></div>
        </div>
      </div>

    </div>
  </div>
</div>
  </div>

        <!-- 睡眠环境 Card -->
        <div class="content-section">

        <!-- Sleep Environment -->
        <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
            <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
            {{ t('sleep_environment') }}
          </h2>
          
          <div class="flex flex-col md:flex-row gap-2">
            <!-- 左侧环境指标 -->
            <div class="w-full md:w-1/3">
              <div class="grid grid-cols-1 gap-2">
                <div v-for="(item, index) in computedSleepEnv" 
                     :key="index"
                     class="flex items-center justify-between p-2.5 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                >
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-500">{{ item.label }}</span>
                    <span v-if="item.description" 
                          class="text-xs text-gray-400 hidden md:inline"
                          :title="item.description"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  </div>
                  <span class="text-base font-medium text-gray-900">{{ item.value }}</span>
                </div>
              </div>
            </div>
  
            <!-- 右侧图表 -->
            <div class="w-full md:w-2/3">
              <div class="bg-white rounded-lg border border-gray-100">
                <div class="h-[140px]">
                  <div :id="EnvChartId" class="w-full h-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
          </div>
      </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-3"></div>
        <span class="text-gray-500">{{ t('page_2') }}</span>
      </footer>
    </div>

    <!-- 第三页和第四页（月趋势） -->
    <div class="report-page">
      <!-- 页眉 -->
      <header class="page-header">
        <div class="flex justify-between items-center px-4 pb-3">
          <h1 class="text-base font-medium text-gray-900">{{ t('sleep_analysis_report') }}</h1>
          <span class="text-black/80 text-sm">{{ formatChineseDate(selectedDate) }}</span>
        </div>
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </header>

      <!-- 月趋势内容第一部分 -->
      <div class="page-content">
        <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
          <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
            <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
            {{ t('monthly_trend') }}
          </h2>
          
          <!-- 使用grid布局将图表分为两列 -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 monthly-print-grid">
            <!-- 总睡眠时间 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('total_sleep_time_alt') }}</h5>
              <div class="h-36">
                <div :id=TstChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 睡速 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('sleep_onset_speed') }}</h5>
              <div class="h-36">
                <div :id=solChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 睡眠规律性 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('sleep_regularity') }}</h5>
              <div class="h-36">
                <div :id=sriChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 睡眠中断时长 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('sleep_interruption_duration') }}</h5>
              <div class="h-36">
                <div :id=wasoChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 睡眠效率 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('sleep_efficiency_t') }}</h5>
              <div class="h-36">
                <div :id=seChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 快速眼动和深睡长 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('rem_and_deep_duration_t') }}</h5>
              <div class="h-36">
                <div :id=deepChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 各睡眠阶段的时长 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ t('sleep_stages_duration_title') }}</h5>
              <div class="h-36">
                <div :id=StageCompChartId class="w-full h-full"></div>
              </div>
            </div>
  
            <!-- 呼吸暂停低通指数 -->
            <div class="trend-chart-container">
              <h5 class="text-sm font-semibold mb-2">{{ isOAHIDevice ? t('obstructive_short') + ' ' : '' }}{{ t('ahi_full_label') }}（{{ isOAHIDevice ? 'OAHI' : 'AHI' }}）</h5>
              <div class="h-36">
                <div :id=ahiChartId class="w-full h-full"></div>
              </div>
            </div></div>
                </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-3"></div>
        <span class="text-gray-500">{{ t('page_3') }}</span>
      </footer>
    </div>

   
  </div>
    <div class="report-page">
      <!-- 页眉 -->
      <header class="page-header">
        <div class="flex justify-between items-center px-4 pb-3">
          <h1 class="text-base font-medium text-gray-900">{{ t('sleep_analysis_report') }}</h1>
          <span class="text-black/80 text-sm">{{ formatChineseDate(selectedDate) }}</span>
        </div>
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
      </header>

      <!-- 月趋势内容第二部分 -->
      <div class="page-content">
        <div class="bg-white rounded-lg shadow-sm p-3 border border-gray-100">
        <h2 class="text-sm font-bold text-gray-900 mb-1.5 flex items-center">
          <span class="w-1 h-4 bg-blue-600 rounded-full mr-2"></span>
          {{ t('monthly_trend') }}
        </h2>
                <!-- 使用grid布局将图表分为两列 -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 monthly-print-grid">
             <!-- 鼾声指数 -->
             <div class="trend-chart-container">
            <h5 class="text-sm font-semibold mb-2">{{ t('snore_index_alt') }}</h5>
            <div class="h-36">
              <div :id=SnoreIndexChartId class="w-full h-full"></div>
            </div>
          </div>
          
          <!-- 睡眠心率变异性 -->
          <div class="trend-chart-container">
            <h5 class="text-sm font-semibold mb-2">{{ t('sleep_hrv') }}</h5>
            <div class="h-36">
              <div :id=hrvBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠呼吸频率 -->
          <div class="trend-chart-container">
            <h5 class="text-sm font-semibold mb-2">{{ t('sleep_respiratory_rate') }}</h5>
            <div class="h-36">
              <div :id=rrBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠心率 -->
          <div class="trend-chart-container">
            <h5 class="text-sm font-semibold mb-2">{{ t('sleep_heart_rate') }}</h5>
            <div class="h-36">
              <div :id=hrBLChartId class="w-full h-full"></div>
            </div>
          </div>

          <!-- 睡眠环境 -->
          <div class="trend-chart-container">
            <h5 class="text-sm font-semibold mb-2">{{ t('sleep_environment') }}</h5>
            <div class="h-36">
              <div :id=envChartId class="w-full h-full"></div>
            </div>
          </div>
          <div class="trend-chart-container min-w-0" data-health-trend="sleepAge">
            <h5 class="text-sm font-semibold mb-2">{{ t('research_trend_sleep_age') }}</h5>
            <div class="h-36">
              <div :id="sleepAgeChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_sleep_age')"></div>
            </div>
          </div>
          <div class="trend-chart-container min-w-0" data-health-trend="agingPace">
            <h5 class="text-sm font-semibold mb-2">{{ t('research_trend_aging_speed') }}</h5>
            <div class="h-36">
              <div :id="agingPaceChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_aging_speed')"></div>
            </div>
          </div>
          <div class="trend-chart-container min-w-0" data-health-trend="recovery">
            <h5 class="text-sm font-semibold mb-2">{{ t('research_trend_recovery') }}</h5>
            <div class="h-36">
              <div :id="recoveryChartId" class="w-full h-full" role="img" :aria-label="t('research_trend_recovery')"></div>
            </div>
          </div>
          </div>  
        </div>
      </div>

      <!-- 页脚 -->
      <footer class="page-footer">
        <div class="h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-3"></div>
        <span class="text-gray-500">{{ t('page_4') }}</span>
      </footer>
    </div>
  </div>
</template>
<script setup>
import { alignMonthlyTrendAxis, monthlyTrendKeys } from '@/plugins/chart/monthly_trend_axis.js'
import { canResearchExport } from '@/modules/cohort/researchPermissions.js'

import { prepareServerReportPdf } from '@/modules/cohort/serverReportPdf.js'
import { bulkPdfError, bulkPdfErrorCode } from '@/modules/cohort/bulkPdfErrors.js'
import { reportCalendarDate } from "@/plugins/orgDashboardApi.js";
  import {onMounted, ref, watch, computed, onBeforeUnmount, nextTick} from 'vue'
  import {createAxiosInstance, wdb_get_history_new} from "@/plugins/axios.js";
  import { setDisplayTimezone } from '@/plugins/utils/timezone'

  import 'v-calendar/style.css'
  import {request_data, fetchUserProfile} from "@/plugins/axios.js";
  import { buildHealthTrendDays, buildSleepAgeTrendOption, buildAgingPaceTrendOption,
    buildRecoveryScoreTrendOption } from '@/plugins/chart/research_health_trends.js'
  import * as echarts from 'echarts'
  import {setStageChartOption} from "@/plugins/chart/stage_chart";
  import {setArtChartOption} from "@/plugins/chart/day_art_chart";
  import {setArt2ChartOption} from "@/plugins/chart/day_art_chart2";
  import {setDayEnvChartOption} from "@/plugins/chart/day_env_chart";
  import {setDayHRVChartOption} from "@/plugins/chart/day_hrv_chart";
  import {setDayRRChartOption} from "@/plugins/chart/day_rr_chart";
  import {setStageAHChartOption} from "@/plugins/chart/stage_ah_chart";
  import {setDayHRChartOption} from "@/plugins/chart/day_hr_chart";
  import {setStageAH2ChartOption} from "@/plugins/chart/stage_ah_chart2";
  import {chart_dates, resetDates} from "@/plugins/chart/variables";
  import {formatDateString} from "@/plugins/utils/utils";
  import { ElMessage } from 'element-plus';
  // import {setSpO2ChartOption} from "@/plugins/chart/spo2_chart";  // 已隐藏血氧图表
  import {setDaySnoreChartOption} from "@/plugins/chart/day_snore_chart";
  import {setTSTChartOption} from "@/plugins/chart/tst_chart";
  import {setStageCompChartOption} from "@/plugins/chart/stage_comp_chart";
  import {setSnoreIndexChartOption} from "@/plugins/chart/snore_index_chart";
  import {setSOLChartOption} from "@/plugins/chart/sol_chart";
  import {setWASOChartOption} from "@/plugins/chart/waso";
  import {setDeepChartOption} from "@/plugins/chart/deep_time_chart";
  import {setEnvChartOption} from "@/plugins/chart/env_chart";
  import {setSEChartOption} from "@/plugins/chart/tst_chart";
  import {setTIBChartOption} from "@/plugins/chart/tib_chart";
  import {setHRVChartOption} from "@/plugins/chart/hrv_chart";
  import {setRRChartOption} from "@/plugins/chart/rr_chart";
  import {setHRChartOption} from "@/plugins/chart/hr_chart";
  import {setAHIChartOption} from "@/plugins/chart/ahi_month_chart";
  import { getBulkOAHIDevices, isOAHIDevice as checkIsOAHIDevice } from '@/plugins/deviceConfig.js'
  
  import { getSubjectBasicInfo } from '@/modules/cohort/cohortApi.js'
  import { basicInfoItems, isBasicInfoResponse } from '@/modules/cohort/basicInfoModel.js'
  const route = useRoute()
  const serverPdf = route.query.serverPdf === '1'
  const reportAbort = new AbortController()
  // Single sticky server PDF failure authority for all readiness dependencies.
  const serverPdfError = ref(''), serverPdfConfigReady = ref(!serverPdf)
  const pendingCharts = ref(0), pendingProfiles = ref(0), pendingQuestionnaires = ref(0)
  // Include the nextTick scheduling gap in server PDF renderer readiness.
  const chartUpdateScheduled = ref(false)
  let chartUpdatePromise = null, chartUpdateDirty = false
  const recordServerPdfError = (error, fallback = 'report') => {
    if (serverPdf && !reportAbort.signal.aborted && !serverPdfError.value) {
      serverPdfError.value = bulkPdfError(bulkPdfErrorCode(error, fallback))
    }
  }
  let prepareReportPdfHook = null
  const cohortProfile = ref(null)
  const profileError = ref('')
  const isLoading = ref(true)
  
  const props = defineProps({
    device: String,
    selections: [Object, Array],
    cohortOrg: String,
    cohortSubjectId: String,
    date: String,
    stage: String,
  
  });
  const keyMap = {
    reporttime: "报告开始计算时间",
    costtime: "报告生成总耗时",
    startc: "数据开始时间",
    endc: "数据结束时间",
    dur: "数据总时长",
    snore_index: "打鼾指数",
    snore_index_onback: "仰卧打鼾指数",
    snore_index_onside: "侧卧打鼾指数",
    posion_onback_time: "仰卧时长",
    posion_onback_rate: "仰卧占比",
    posion_onside_time: "侧卧时长",
    posion_onside_rate: "侧卧占比",
  
    sigtype_0: "无信号",
    sigtype_1: "低信号",
    sigtype_2: "正常信号",
    sigtype_3: "微体动信号",
    sigtype_4: "强体动信号",
    onbedlevel: "在床阈值",
    err_log: "错误信息",
    avg_temp: "平均温度",
    avg_humi: "平均湿度",
    avg_illu: "睡眠时平光照",
    high_illu: "强光照睡眠",
    sleep_time: "有效睡眠总时间",
    ahi_count: "呼吸事件总数",
    ahi: "综合AHI",
    long_artifact: "体动次数",
    short_artifact: "微体动次数",
    sri: "SRI",
    quality: "睡眠质量分",
    qualitylogs: "睡眠质量细则",
    sleep_dur_0: "睡眠分期_体动和无效时长",
    ahi_count_0: "体动呼吸事件次数（无效）",
    sleep_dur_1: "睡眠分期_Wake时",
    ahi_count_1: "Wake呼吸事件次数（无效）",
    sleep_dur_2: "睡眠分期_REM时长",
    ahi_count_2: "REM期呼事件次数",
    ahi_2: "REM期呼吸指数",
    sleep_dur_3: "睡眠分期_N1时长",
    sleep_dur_4: "睡眠分期_N2时长",
    sleep_dur_5: "睡眠分期_N3时长",
    ahi_count_345: "NREM期呼吸事件次数",
    ahi_345: "NREM期呼吸指数",
    odi_count: "ODI次数",
    odi: "ODI指数",
    min_spo2: "最低血饱和度",
    mean_spo2: "平均血氧饱和度",
    hrv_rmssd_mean: "hrv_rmssd",
    hrv_morning: "hrv_morning",
    hrv_evening: "hrv_evening",
    hrv_delta: "hrv_delta",
    hrv_ratio_mean: "hrv_ratio",
    sleep_range: "睡眠区间调试信息",
    push_message: "push消息",
  };
  const selectedDevice = ref(props.device)
  const selectedReportDate = ref(props.date); // 选中的告日期
  console.log(selectedReportDate.value) 
  const reports = ref({}); // 告列表
  const reportDates = ref([]); // 报告日期列表
  const selections = ref(props.selections || {})
  const userinfo = ref(selections.value?.userinfo ? { ...selections.value.userinfo } : {})
  
  const deviceinfo = ref(selections.value?.['deviceinfo'] || {})
  const summmary = ref({})
  const endData = ref({})
  const data = ref({})
  const chartId = ref(`stage_chart_${Math.random().toString(36).substr(2, 9)}`);
  const ArtChartId = ref(`art_chart_${Math.random().toString(36).substr(2, 9)}`);
  const EnvChartId = ref(`env_chart_${Math.random().toString(36).substr(2, 9)}`);
  const HrvChartId = ref(`hrv_chart_${Math.random().toString(36).substr(2, 9)}`);
  const RrChartId = ref(`rr_chart_${Math.random().toString(36).substr(2, 9)}`);
  const AhChartId = ref(`ah_chart_${Math.random().toString(36).substr(2, 9)}`);
  const HrChartId = ref(`hr_chart_${Math.random().toString(36).substr(2, 9)}`);
  const rrBLChartId = ref(`rr_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
  const hrBLChartId = ref(`hr_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
  const hrvBLChartId = ref(`hrv_bl_chart_${Math.random().toString(36).substr(2, 9)}`);
  const ahiChartId = ref(`ahi_chart_${Math.random().toString(36).substr(2, 9)}`);
  // const spo2ChartId = ref(`spo2_chart_${Math.random().toString(36).substr(2, 9)}`);  // 已隐藏血氧图表
  const SnoreChartId = ref(`snore_chart_${Math.random().toString(36).substr(2, 9)}`);
  const TstChartId = ref(`tst_chart_${Math.random().toString(36).substr(2, 9)}`);
  const StageCompChartId = ref(`stage_comp_chart_${Math.random().toString(36).substr(2, 9)}`);
  const SnoreIndexChartId = ref(`snore_index_chart_${Math.random().toString(36).substr(2, 9)}`);
  const solChartId = ref(`sol_chart_${Math.random().toString(36).substr(2, 9)}`);
  const wasoChartId = ref(`waso_chart_${Math.random().toString(36).substr(2, 9)}`);
  const seChartId = ref(`se_chart_${Math.random().toString(36).substr(2, 9)}`);
  const envChartId = ref(`env_month_chart_${Math.random().toString(36).substr(2, 9)}`);
  const deepChartId = ref(`deep_chart_${Math.random().toString(36).substr(2, 9)}`);
  const sriChartId = ref(`sri_chart_${Math.random().toString(36).substr(2, 9)}`);
  const sleepAgeChartId = ref(`sleep_age_chart_${Math.random().toString(36).slice(2, 11)}`)
  const agingPaceChartId = ref(`aging_pace_chart_${Math.random().toString(36).slice(2, 11)}`)
  const recoveryChartId = ref(`recovery_chart_${Math.random().toString(36).slice(2, 11)}`)
  const reports_data = ref([])
  const selectedDate = ref(null)
// 添加新的响应式变量
const essScore = ref(null)
const psqiScore = ref(null)

// 防止重复请求的标志
const isLoadingTrend = ref(false);
const lastTrendRequest = ref(null);

  // 判断是否有报告
  const hasReport = (date) => {
    return reportDates.value.some(reportDate =>
        new Date(reportDate).toDateString() === new Date(date).toDateString()
    )
  }
  
  const disableDate = (date) => {
    // 可选择禁用没��报告的日期
    return !hasReport(date)
  }
  // 日期配置
  
  // 计算最早和最晚的报告日期
  const minDate = computed(() => {
    if (reportDates.value.length === 0) return null
    return new Date(Math.min(...reportDates.value.map(d => new Date(d))))
  })
  
  const maxDate = computed(() => {
    if (reportDates.value.length === 0) return null
    return new Date(Math.max(...reportDates.value.map(d => new Date(d))))
  })
  
  // 修改用日期的定义
  const disabledDates = computed(() => {
    // 生成一个日期范围内的所有日期
    const start = minDate.value
    const end = maxDate.value
    const allDates = []
  
    if (start && end) {
      const current = new Date(start)
      while (current <= end) {
        const dateString = current.toISOString().split('T')[0]
        if (!reportDates.value.includes(dateString)) {
          allDates.push(new Date(current))
        }
        current.setDate(current.getDate() + 1)
      }
    }
  
    return allDates
  })
  
  // 日历标记
  const attributes = computed(() => [
    {
      dates: reportDates.value.map(date => new Date(date)),
      dot: {
        color: 'green',
        class: 'report-dot'
      },
      popover: {
        label: t('has_report')
      }
    }
  ])
  
  // 从 final 数据中提取 summary 相关信息
  function extractSummaryFromFinal(finalData) {
    if (!finalData) return {}
    
    // 精简报告接口提供顶层 bpm_min；旧完整报告保留 hr_all.min，兼容两种结构。
    const min_sleep_heart_rate = finalData.bpm_min ?? finalData.hr_all?.min
    
    // 有效数值取整；缺失值保留为 null，避免被误显示成 0。
    const roundNum = (val) => {
      if (val == null || val === '') return null
      const num = parseFloat(val)
      return Number.isFinite(num) ? Math.round(num) : null
    }
    
    return {
      min_sleep_heart_rate: roundNum(min_sleep_heart_rate),
      avg_sleep_heart_rate: roundNum(finalData.bpm_avg),
      avg_hrv_rmssd: roundNum(finalData.hrv_rmssd_mean),
      avg_hrv_radio: roundNum(finalData.hrv_ratio_mean)
    }
  }

  //更新数据
  function updateEndData() {
    const mappedData = {};
    data.value = reports.value['final']
    // 同步全局显示时区，让所有图表 axisLabel/tooltip 跟随当前报告的时区
    setDisplayTimezone(data.value && data.value.timezone)
    // 分配映射的数据
    if (data.value) {
      Object.keys(keyMap).forEach(key => {
        if (key in data.value) {
          mappedData[keyMap[key]] = data.value[key];
        }
      })
    }
    // 添加'其他'分类
    endData.value = mappedData;
  }
  
  //监听日期 选择被试
  watch([() => selectedReportDate.value, () => selectedDevice.value], async ([newDate, newDevice], [oldDate, oldDevice]) => {
    if (newDevice == "" || newDevice != oldDevice) {
    } else if (oldDate !== newDate) {
      await getReports()
  
    }
  }, {immediate: true});
  //监听日期
  watch(() => props.date, (newVal) => {
    // 当数据变化时，重新渲染图表
    selectedReportDate.value = newVal
    selectedDate.value = selectedReportDate.value
  
    getReports()
  
  }, {deep: true});
  watch(() => props.device, async (newVal) => {
    // 当数据变化时，重新渲染图表
    selectedDevice.value = newVal
    await updateDeviceType() // 更新设备类型
    changeDate()
    getReports()
  }, {deep: true});
  watch(() => props.selections, (newVal) => {
    selections.value = newVal || {}
    if (newVal?.userinfo) userinfo.value = { ...newVal.userinfo }
    deviceinfo.value = newVal?.deviceinfo || {}
    getReports()
  }, {deep: true});
  
  async function changeDate() {
    reportDates.value = []
    if(selectedReportDate.value){
     return       await getReports()

    }
     selectedReportDate.value = ""
    const response = await request_data(`/list_reports/${selectedDevice.value}/`, '计算服务器1');
    reportDates.value = response.nodes;
    if (response.nodes.length > 0 && selectedReportDate.value === '') {
      selectedReportDate.value = response.nodes[0];
      await getReports()
  
    }
    return response
  }
  
  async function getReportList(reportdate) {
    try {
      // 确保 reportdate 是有效的日期字符串或时间戳
      if (!reportdate) {
        return;
      }
      
      // ✅ 防止重复请求
      const requestKey = `${selectedDevice.value}-${reportdate}`;
      if (isLoadingTrend.value && lastTrendRequest.value === requestKey) {
        console.log('跳过重复的趋势数据请求');
        return;
      }
      
      isLoadingTrend.value = true;
      lastTrendRequest.value = requestKey;
  
      reports_data.value = {};
      // 将 reportdate 转换为 Date 对象并验证
      let selectedDate = new Date(reportCalendarDate(reportdate) + 'T12:00:00');
      if (isNaN(selectedDate.getTime())) {
        console.error('Invalid report date:', reportdate);
        return;
      }
  
      // 计算起始日期和结束日期
      let endDate = new Date(selectedDate);
      let startDate = new Date(selectedDate);
      startDate.setDate(selectedDate.getDate() - 29);
  
      // 验证期范围
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid date range');
        return;
      }
  
      // 格式化日期为 yyyy-mm-dd
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
  
      let formattedStartDate = formatDate(startDate);
      let formattedEndDate = reportCalendarDate(reportdate);
  
      // 验证 selectedDevice
      if (!selectedDevice?.value) {
        console.error('Selected device is required');
        return;
      }
  
      // ✅ 使用优化接口获取趋势数据
      console.log(`准备获取趋势数据: ${selectedDevice.value}, ${formattedStartDate} ~ ${formattedEndDate}`);
      
      try {
        // 尝试使用新的优化接口
        const { getReportTrendData, convertTrendDataToLegacy } = await import('@/plugins/orgDashboardApi.js');
        
        const days = Math.ceil((new Date(formattedEndDate) - new Date(formattedStartDate)) / (1000 * 60 * 60 * 24)) + 1;
        
        console.log(`使用优化接口获取 ${days} 天数据`);
        const trendData = await getReportTrendData(selectedDevice.value, {
          days: days,
          endDate: formattedEndDate,
        anchorDate: formattedEndDate
        });
        
        // 转换为原有格式，兼容现有代码
        const convertedData = convertTrendDataToLegacy(trendData);
        
        console.log('=== 优化接口返回数据 ===');
        console.log('数据对象:', convertedData);
        console.log('数据键数量:', Object.keys(convertedData).length);
        if (lastTrendRequest.value === requestKey && selectedDevice.value + '-' + selectedReportDate.value === requestKey) {
        reports_data.value = convertedData;
      }
        
      } catch (error) {
          console.warn('个人趋势加载失败:', error);
        if (serverPdf) recordServerPdfError(error);
        if (lastTrendRequest.value === requestKey) reports_data.value = {};
      }

    } catch (error) {
      console.error('Error in getReportList:', error);
      if (serverPdf) recordServerPdfError(error);
      // 这里可以添加错误处理逻辑，比如显示错误消息给用户
    } finally {
      // ✅ 请求完成，重置标志
      if (lastTrendRequest.value === `${selectedDevice.value}-${reportdate}`) isLoadingTrend.value = false;
    }
  }
  
  
  // 修改获取数据和初始化图表的方法
  let detailRequestGeneration = 0;
  let serverPdfCohortProfileAwaited = false;
async function getReports() {
  const generation = ++detailRequestGeneration;
  const device = selectedDevice.value;
  const date = selectedReportDate.value;
  const isCurrent = () => generation === detailRequestGeneration && selectedDevice.value === device && selectedReportDate.value === date;
  chartUpdateGeneration++
  for (const key of ['sleepAge', 'agingPace', 'recovery']) chartInstances.value[key]?.clear()
  reports_data.value = {};
  reports.value = {};
  summmary.value = {};
    try {
      isLoading.value = true
      if (!props.device) return

      // Server PDF scores depend only on device/date, not report/trend/profile data.
      // Start now; the existing questionnaire pending/error state still gates printing.
      if (serverPdf) fetchQuestionnaireScores().catch(err => {
        console.warn('获取问卷分数失败:', err);
      })
  
      const reportdate = selectedReportDate.value ? selectedReportDate.value : "_";
      
      // ✅ 并行获取报告数据、趋势数据和报告信息，提升加载速度
      const reportUrl = `/devicereport/${selectedDevice.value}/${reportdate}/`
      // Validate in the request promise, not after all other dependencies settle:
      // a missing final must fail immediately even if trend/profile is still pending.
      const reportRequest = serverPdf
        ? createAxiosInstance('计算服务器1').get(reportUrl, { signal: reportAbort.signal }).then(response => {
            const payload = response.data?.data
            const final = payload?.final
            if (!final || typeof final !== 'object' || Array.isArray(final) || !Object.keys(final).length) {
              throw bulkPdfError('report')
            }
            return payload
          })
        : request_data(reportUrl, '计算服务器1')
      if (serverPdf && props.cohortOrg) serverPdfCohortProfileAwaited = true
      const [reportResponse] = await Promise.all([
        reportRequest,
        getReportList(selectedReportDate.value), // 并行获取趋势数据（优化接口）
        fetchReportInfo().catch(err => { // 并行获取报告信息（容错处理）
          console.warn('获取报告信息失败:', err);
          return null;
        }),
      ]);
      
      if (!isCurrent()) return;
      // Keep the single server PDF cohort profile attempt and its readiness/error state.
      reports.value = reportResponse;
      // getReportList 和 fetchReportInfo 已经在 Promise.all 中并行执行
      
      // Preserve ordinary/native report initialization order.
      if (!serverPdf) fetchQuestionnaireScores().catch(err => {
        console.warn('获取问卷分数失败:', err);
      })

      if (reports.value['final']) {
        // 从 final 数据中提取 summary 相关信息
        summmary.value = extractSummaryFromFinal(reports.value['final'])
        updateEndData()
        // 确保 DOM 已更新后再初始化图表
        await nextTick()
      if (!isCurrent()) return;
        await updateCharts()
      }
    } catch (error) {
      console.error('Error fetching reports:', error)
      if (serverPdf) recordServerPdfError(error)
    } finally {
      if (isCurrent()) isLoading.value = false
    }
  }
  // 监听 selectedReportDate 变化
  watch(() => selectedReportDate.value, (newVal) => {
    if (newVal) {
      selectedDate.value = new Date(newVal)
    }
  }, { immediate: true })
  
  
  onMounted(async () => {
    if (serverPdf) return // Server PDF mode uses the single awaited initialization below.
    await updateDeviceType() // 初始化设备类型
    changeDate()
  });
  const formatTime = (timestamp, tz) => {
    if (!timestamp) return '--:--';
  
    try {
      // 后端 timezone：北京=0, 美西=15。utc_offset = 8 - timezone
      const tzVal = (tz != null) ? tz : (data.value && data.value.timezone) || 0;
      const offsetH = 8 - (Number(tzVal) || 0);
      const date = new Date(timestamp * 1000 + offsetH * 3600 * 1000);
  
      // 检查是否是有效日期
      if (isNaN(date.getTime())) {
        return '--:--';
      }
  
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('时间格式化错误:', error);
      return '--:--';
    }
  }
  
  function formatMinutes(minutes) {
    if (Number.isFinite(minutes)) {  // 查minutes是否为一个有限的数
      const roundedMinutes = Math.round(minutes);
      const hours = Math.floor(roundedMinutes / 60);
      const remainingMinutes = roundedMinutes % 60;
  
      if (hours === 0) {
        return `${remainingMinutes}${t('minutes_suffix')}`;
      }
  
      return `${hours}${t('hour_char')} ${remainingMinutes}${t('minute_char')}`;
    }
    return '';
  }
  
  const getSleepEfficiency = (tst, tib) => {
    if (!tst || !tib) return '--'
    return ((tst / tib) * 100).toFixed(0)
  }
  // 计算属性
  const computedSleepAnalysis = computed(() => {
    if (!data.value) return []
  
    return [
      // 时间相关
      {label: t('lights_off_time'), value: formatTime(data.value.sleep_intention_time)},
      {label: t('lights_on_time'), value: formatTime(data.value.intention_leave_time)},
      {label: t('total_sleep_time_tst'), value: formatMinutes(data.value.tst)},
      {label: t('sleep_efficiency_se'), value: `${getSleepEfficiency(data.value.tst, data.value.tib)}%`},
      
      // 睡眠质量相关
      {label: t('sleep_onset_sol'), value: formatMinutes(data.value.sol)},
      {label: t('sleep_interruption_waso'), value: formatMinutes(data.value.waso)},
      {label: t('deep_sleep_t'), value: formatMinutes(data.value.deep_time)},
      {label: t('rem_latency_label'), value: formatMinutes(data.value.rem_latency)},
      
      // 睡眠评分相
      {label: t('sleep_regularity_sri'), value: (data.value.sri != null && data.value.sri >= 0) ? `${data.value.sri}%` : '--'},
      {label: t('sleep_fragmentation_sfi'), value: data.value.sfi?.toFixed(1) || '--'}
    ]
  })
  
  const computedSleepPhases = computed(() => {
    if (!data.value) return []
  
    const totalTime = data.value.tst || 0
    return [
      {
        label: t('awake'),
        value: formatMinutes(data.value.sleep_dur_1),
        percentage: data.value.sleep_dur_1_efficiency ? `${((data.value.sleep_dur_1 / totalTime) * 100).toFixed(0)}%` : '--'
      },
      {
        label: t('rem_sleep'),
        value: formatMinutes(data.value.rem_time),
        percentage: data.value.rem_efficiency ? `${(data.value.rem_efficiency * 100).toFixed(0)}%` : '--'
      },
      {
        label: t('light_sleep'),
        value: formatMinutes(data.value.core_time),
        percentage: data.value.core_efficiency ? `${(data.value.core_efficiency * 100).toFixed(0)}%` : '--'
      },
      {
        label: t('deep_sleep_t'),
        value: formatMinutes(data.value.deep_time),
        percentage: data.value.deep_efficiency ? `${(data.value.deep_efficiency * 100).toFixed(0)}%` : '--'
      }
    ]
  })
  
  // 添加设备号判断的响应式变量
  const isOAHIDevice = ref(false)

  // 检查设备是否为 OAHI 设备
  const updateDeviceType = async () => {
    if (selectedDevice.value) {
      if (serverPdf) {
        const devices = await getBulkOAHIDevices(selectedDevice.value, { signal: reportAbort.signal })
        if (reportAbort.signal.aborted) return
        isOAHIDevice.value = devices.includes(String(selectedDevice.value))
        serverPdfConfigReady.value = true
        // Report data may already be loaded. Populate charts only after the
        // authoritative classification, never print charts made with a fallback.
        await updateCharts()
      } else {
        isOAHIDevice.value = await checkIsOAHIDevice(selectedDevice.value)
      }
    }
  }

  // 预处理 OAHI 数据，将 oahi 字段映射为 ahi 字段供图表使用
  const preprocessOAHIData = (data) => {
    if (!data || !Array.isArray(data)) return data
    
    return data.map(item => {
      if (item && typeof item === 'object') {
        return {
          ...item,
          ahi: item.oahi || item.OAHI || item.ahi,
          ahi_2: item.oahi_2 || item.ahi_2,
          ahi_345: item.oahi_345 || item.ahi_345
        }
      }
      return item
    })
  }
  
  const computedBreathingHealth = computed(() => {
    if (!data.value) return []
  
    // 根据设备号选择使用 AHI 还是 OAHI 数据
    if (isOAHIDevice.value) {
      return [
        {label: t('oahi'), value: (data.value.oahi || data.value.OAHI)?.toFixed(1) || '--'},
        {label: 'NREM OAHI', value: data.value.oahi_345?.toFixed(1) || '--'},
        {label: 'REM OAHI', value: data.value.oahi_2?.toFixed(1) || '--'},
        {label: t('snore_index_si'), value: data.value.bcgsnore_index?.toFixed(1) || '--'},
        {
          label: t('sleep_avg_rr'),
          value: data.value.avg_resp ? `${data.value.avg_resp.toFixed(1)}${t('bpm_unit')}` : '--'
        }
      ]
    } else {
    return [
      {label: t('ahi'), value: data.value.ahi?.toFixed(1) || '--'},
      {label: 'NREM AHI', value: data.value.ahi_345?.toFixed(1) || '--'},
      {label: 'REM AHI', value: data.value.ahi_2?.toFixed(1) || '--'},
      {label: t('snore_index_si'), value: data.value.bcgsnore_index?.toFixed(1) || '--'},
      {
        label: t('sleep_avg_rr'),
        value: data.value.avg_resp ? `${data.value.avg_resp.toFixed(1)}${t('bpm_unit')}` : '--'
      }
    ]
    }
  })
  
  
  // 格式化数字为整数的函数
  const formatInteger = (val) => {
    if (val == null || val === '' || val === undefined) return null
    const num = parseFloat(val)
    if (isNaN(num)) return null
    return Math.round(num).toString()
  }
  
  const computedSleepPeriodIndicators = computed(() => {
    if (!data.value) return []
  
    const minHeartRate = formatInteger(summmary.value.min_sleep_heart_rate)
    const avgHeartRate = formatInteger(summmary.value.avg_sleep_heart_rate)
    // HRV RMSSD：优先从 final/online 的 hrv_rmssd_mean 获取，其次从 summary 的 avg_hrv_rmssd 获取，保留两位小数
    const hrvRmssd = (() => {
      const value = data.value.hrv_rmssd_mean != null 
        ? parseFloat(data.value.hrv_rmssd_mean) 
        : (summmary.value.avg_hrv_rmssd != null 
            ? parseFloat(summmary.value.avg_hrv_rmssd) 
            : null);
      return value != null ? value.toFixed(2) : null;
    })()
    // HRV LF/HF 直接从 hrv_ratio_mean 获取，保留两位小数
    const hrvRadio = data.value.hrv_ratio_mean != null ? parseFloat(data.value.hrv_ratio_mean).toFixed(2) : null
  
    return [
      {
        label: t('sleep_min_hr'),
        value: minHeartRate !== null ? `${minHeartRate}${t('bpm_unit')}` : '--'
      },
      {
        label: t('sleep_avg_hr'),
        value: avgHeartRate !== null ? `${avgHeartRate}${t('bpm_unit')}` : '--'
      },
      {
        label: t('sleep_hrv_rmssd'), 
        value: hrvRmssd !== null ? `${hrvRmssd}${t('ms_unit')}` : '--'
      },
      {
        label: t('sleep_hrv_lfhf'), 
        value: hrvRadio !== null ? hrvRadio : '--'
      }
    ]
  })
  const computedSleepEnv = computed(() => {
    if (!data.value) return []
    // 打印版：同 OrgReportDetail，兜底 NaN/null → "--"
    const fmt = (val, unit) => {
      const num = parseFloat(val)
      return Number.isFinite(num) ? `${Math.round(num)}${unit}` : '--'
    }
    const fmtRatio = (val) => {
      const num = parseFloat(val)
      return Number.isFinite(num) ? `${Math.round(num * 100)}%` : '--'
    }
    return [
      { label: t('avg_temperature'), value: fmt(data.value.avg_temp, '°C') },
      { label: t('avg_humidity'), value: fmt(data.value.avg_humi, '%') },
      { label: t('light_affect_ratio'), value: fmtRatio(data.value.high_illu) },
    ]
  })
  let reportResizeObserver
  // 处理窗口大小变化
  const handleResize = () => {
    if (window.matchMedia('print').matches || isDownloading.value) return
    // 触发所有图表 resize
    Object.values(chartInstances.value).forEach(chart => {
      // Native print temporarily replaces canvases with images. Resizing those
      // display:none elements would poison the next download with a 100px axis.
      if (chart && chart.getDom().offsetParent !== null) {
        try {
          chart.resize()
        } catch (error) {
          console.warn('图表 resize 失败:', error)
        }
      }
    })
  }
  
  // 销毁图表实例
  const destroyCharts = () => {
    Object.values(chartInstances.value).forEach(chart => chart?.dispose())
    chartInstances.value = {}
  }
  // 图表引用
  const chartRefs = ref({})
  const chartInstances = ref({})
  // 初始化所有图表
  const initCharts = () => {
    const chartIds = {
      stage: chartId.value,
      art: ArtChartId.value,
      hr: HrChartId.value,
      hrv: HrvChartId.value,
      rr: RrChartId.value,
      // spo2: spo2ChartId.value,  // 已隐藏血氧图表
      ah: AhChartId.value,
      snoreIndex: SnoreIndexChartId.value,
      env: envChartId.value,
      env_day: EnvChartId.value,
      tst: TstChartId.value,
      stageComp: StageCompChartId.value,
      deep: deepChartId.value,
      se: seChartId.value,
      sol: solChartId.value,
      waso: wasoChartId.value,
      snore: SnoreChartId.value,
      sri: sriChartId.value,
      ahi: ahiChartId.value,
      rrbl: rrBLChartId.value,
      hrbl: hrBLChartId.value,
      hrvbl: hrvBLChartId.value,
      sleepAge: sleepAgeChartId.value,
      agingPace: agingPaceChartId.value,
      recovery: recoveryChartId.value,
    }
  
    // 确保先销毁现有实例
    destroyCharts()
  
    // 重新初始化图表
    Object.entries(chartIds).forEach(([key, id]) => {
      const el = document.getElementById(id)
      if (el) {
        const chart = echarts.init(el)
        chartInstances.value[key] = chart
        
        // 设图表响应容器大小
        chart.setOption({
          grid: {
            containLabel: true,
            left: '3%',
            right: '3%'
          }
        })
      }
    })
  }
  
  
  // 更所有图表
  // Keep upstream stale-context protection inside each actual render pass;
  // the server PDF scheduler below still owns the shared drain promise.
  let chartUpdateGeneration = 0
  const performChartUpdate = async () => {
    if (reportAbort.signal.aborted) return
    const generation = ++chartUpdateGeneration
    const context = `${selectedDevice.value}-${selectedReportDate.value}`
    if ((serverPdf && !serverPdfConfigReady.value) || !Object.keys(data.value).length) return
    pendingCharts.value++
    try {
    // 确保 DOM 已更新
    await nextTick()
    // Unmount may occur during this implementation's own tick, after the
    // scheduler checked its signal. Never recreate disposed charts.
    if (reportAbort.signal.aborted) return
    // 如果图表实例不存在，重新初始化
    if (Object.keys(chartInstances.value).length === 0) {
      initCharts()
    }
    // 先初始化图表日期（使用 M/D 格式）
    initChartDates(reports_data.value, selectedReportDate.value)
  
    // 确保 reports_data.value 是正确的格式
    if (reports_data.value && typeof reports_data.value === 'object' && !Array.isArray(reports_data.value)) {
      // 检查是否有 reports 字段（说明可能是原始响应对象）
      if ('reports' in reports_data.value && Array.isArray(reports_data.value.reports)) {
        console.warn('⚠️ reports_data.value 仍然是原始响应对象，需要转换！')
        // 重新转换数据
        try {
          const { convertTrendDataToLegacy } = await import('@/plugins/orgDashboardApi.js');
          const convertedData = convertTrendDataToLegacy(reports_data.value);
          reports_data.value = convertedData;
          console.log('✅ 已重新转换数据，新键:', Object.keys(reports_data.value));
          // 重新初始化图表日期
          initChartDates(reports_data.value, selectedReportDate.value)
        } catch (error) {
          console.error('❌ 转换数据失败:', error);
        }
      }
    }
    
    // parseReportsToDisplay 处理数组格式，但我们现在的数据是对象格式
    // 如果 reports_data.value 是对象，跳过 parseReportsToDisplay
    // 因为对象格式的数据已经通过 initChartDates 处理了
    if (Array.isArray(reports_data.value)) {
    parseReportsToDisplay(reports_data.value)
    } else {
      console.log('reports_data.value 是对象格式，跳过 parseReportsToDisplay')
    }
    const baseData = {
      start: data.value['starttime'],
      end: data.value['endtime'],
      stages: data.value['stage_details'],
      nointention: (data.value['intention_middle'] && data.value['intention_middle'].length > 0) ? data.value['intention_middle'] : null,
      leavebed: (data.value['leavebed'] && data.value['leavebed'].length > 0) ? data.value['leavebed'] : null,
      signalMissingIntervals: data.value['signal_missing_intervals'] || [],
    }
    await new Promise(resolve => setTimeout(() => {
      if (reportAbort.signal.aborted || generation !== chartUpdateGeneration ||
          context !== `${selectedDevice.value}-${selectedReportDate.value}`) { resolve(); return }
      const healthPoints = buildHealthTrendDays(reports_data.value, reportCalendarDate(selectedReportDate.value))
      const healthLabels = {
        age: t('research_trend_sleep_age'), pace: t('research_trend_aging_speed'),
        avg28d: t('research_trend_28d'), recovery: t('research_trend_recovery'),
        years: t('research_trend_years'), yearsPerYear: t('research_trend_years_per_year'),
      }
      // 更新每个图表
      Object.entries(chartInstances.value).forEach(([key, chart]) => {
        if (!chart) return
  
        try {
          switch (key) {
            case 'sleepAge':
              chart.setOption(buildSleepAgeTrendOption(healthPoints, healthLabels), { notMerge: true })
              break
            case 'agingPace':
              chart.setOption(buildAgingPaceTrendOption(healthPoints, healthLabels), { notMerge: true })
              break
            case 'recovery':
              chart.setOption(buildRecoveryScoreTrendOption(healthPoints, healthLabels), { notMerge: true })
              break
            case 'stage':
              setStageChartOption(chart, {...baseData})
              // 确保图表渲染完成
              chart.resize()
              break
            case 'art':
              // 根据数据类型选择不同的渲染方式
              if (data.value['artifact_series']) {
                setArt2ChartOption(chart, {
                  ...baseData,
                  segments: data.value['artifact_series']
                })
              } else {
                setArtChartOption(chart, {
                  ...baseData,
                  segments: data.value['artifact_series']
                })
              }
              // 确保图表渲染完成
              chart.resize()
              break
            case 'hr':
              setDayHRChartOption(chart, {
                ...baseData,
                segments: data.value.bpm_series,
                avg: data.value.bpm_avg
              })
              break
            case 'hrv':
              setDayHRVChartOption(chart, {
                ...baseData,
                segments: data.value.hrv_rmssd,
                avg: data.value.hrv_rmssd_mean
              })
              break
            case 'rr':
              setDayRRChartOption(chart, {
                ...baseData,
                segments: data.value.resp_series,
                avg: data.value.avg_resp
              })
              break
            // 已隐藏血氧图表，spo2 case 不再渲染
            // case 'spo2':
            //   setSpO2ChartOption(chart, {
            //     ...baseData,
            //     spo2: data.value.spo2_data
            //   })
            //   break
            case 'ah':
              // 根据设备号选择使用 AHI 还是 OAHI 数据
              if (isOAHIDevice.value) {
                // OAHI 设备使用 oahi 相关数据
                if (data.value.oahi_series) {
                  setStageAH2ChartOption(chart, {
                    ...baseData,
                    ah: data.value.oahi_series || data.value.OAHI_event
                  })
                } else {
                  setStageAHChartOption(chart, {
                    ...baseData,
                    ah: data.value.OAHI_event || data.value.oahi_events
                  })
                }
              } else {
                // 普通设备使用 ahi 数据
              if (data.value.ahi_series) {
                setStageAH2ChartOption(chart, {
                  ...baseData,
                  ah: data.value['ahi_series'] || data.value['ahi_events']
                })
              } else {
                setStageAHChartOption(chart, {
                  ...baseData,
                  ah: data.value.ahi_events
                })
                }
              }
              break
            case 'snoreIndex':
              setSnoreIndexChartOption(reports_data.value, chart, chart_dates);
  
              break
            case 'env':
              setEnvChartOption(reports_data.value, chart, chart_dates)
              break
            case 'env_day':
              setDayEnvChartOption(chart, {
                ...baseData,
                segments: data.value.thi_series
              })
              break
              // 趋势图表
            case 'tst':
              setTSTChartOption(reports_data.value, chart, chart_dates)
              break
            case 'stageComp':
              setStageCompChartOption(reports_data.value, chart, chart_dates)
              break
            case 'deep':
              setDeepChartOption(reports_data.value, chart, chart_dates)
              break
            case 'se':
              setSEChartOption(reports_data.value, chart, chart_dates)
              break
            case 'sol':
              setSOLChartOption(reports_data.value, chart, chart_dates)
              break
            case 'waso':
              setWASOChartOption(reports_data.value, chart, chart_dates)
              break
            case 'ahi':
              // 为 OAHI 设备预处理数据
              if (isOAHIDevice.value) {
                const processedData = preprocessOAHIData(reports_data.value)
                setAHIChartOption(processedData, chart, chart_dates)
              } else {
              setAHIChartOption(reports_data.value, chart, chart_dates)
              }
              break
            case 'rrbl':
              setRRChartOption(reports_data.value, chart, chart_dates)
              break
            case 'hrbl':
              setHRChartOption(reports_data.value, chart, chart_dates)
              break
            case 'hrvbl':
              setHRVChartOption(reports_data.value, chart, chart_dates)
              break
            case 'snore':
              setDaySnoreChartOption(chart, {
                ...baseData,
                snore: data.value['bcgsnore_events']
              })
              break
            case 'sri':
              setTIBChartOption(reports_data.value, chart, chart_dates);
              break
          }
          if (monthlyTrendKeys.has(key)) alignMonthlyTrendAxis(chart, healthPoints)
          if (serverPdf) chart.setOption({ animation: false })
        } catch (error) {
          console.error(`更新图表 ${key} 失败:`, error)
          if (serverPdf) recordServerPdfError(error, 'render')
        }
        finally{
          isLoading.value = false
  
        }
      })
      resolve()
    }, 100))
    } finally { pendingCharts.value-- }
  }
  
  // Server PDF callers share one drain promise. Calls before the tick are folded into
  // its first pass; calls during a pass request another pass with current data.
  // Keep the ordinary/native-print update path unchanged.
  const updateCharts = () => {
    if (!serverPdf) return performChartUpdate()
    if (reportAbort.signal.aborted) return Promise.resolve()
    chartUpdateDirty = true
    if (chartUpdatePromise) return chartUpdatePromise
    chartUpdateScheduled.value = true
    chartUpdatePromise = (async () => {
      try {
        while (chartUpdateDirty && !reportAbort.signal.aborted) {
          await nextTick()
          if (reportAbort.signal.aborted) break
          // Consume all changes up to this tick, not changes arriving in the
          // awaited update (which must survive to the next iteration).
          chartUpdateDirty = false
          await performChartUpdate()
        }
      } catch (error) {
        recordServerPdfError(error, 'render')
        throw error
      } finally {
        chartUpdateDirty = false
        chartUpdateScheduled.value = false
        chartUpdatePromise = null
      }
    })()
    // Some Vue watcher invocations don't consume their return value. Observe
    // rejection here without changing the promise/error seen by awaiting callers.
    chartUpdatePromise.catch(() => {})
    return chartUpdatePromise
  }

  const parseReportsToDisplay = (reports) => {
    //console.log("History:parseReportsToDisplay");
  
    // 传入了报告列表，是长时间趋势图模式
    var i;
    if (reports && reports.length > 0) {
      this.setData({
        reportCount: reports ? reports.length : 0,
      });
      let max_chart_days = 0
      if (this.data.period === 'week') {
        max_chart_days = 7;
      } else if (this.data.period === 'month') {
        max_chart_days = 30;
      } else if (this.data.period === 'year') {
        max_chart_days = 365;
      }
  
      //生成空的日期序列，用于各图表的日期轴
      var today = new Date(reports[0].end * 1000); //TODO：注意：这里使用第一份有效报告作为横轴最新节点，可能会导致跟取数据库的时间区间对不齐
      today.setHours(0, 0, 0, 0);
      var todayTS = today.getTime() / 1000; //按今天零点对齐
      resetDates(); //先清空
      for (i = 0; i < max_chart_days; i++) {
        var shortDate = formatDateString(todayTS * 1000, "yyyyMMdd", false);
        chart_dates[shortDate] = -1;
        today.setDate(today.getDate() - 1);
      todayTS = today.getTime() / 1000;
      }
      //在空日期序列中填充有效报告数据的索引
      todayTS = today.getTime() / 1000; //重新按今天零点对齐
      reports.forEach((value, index, array) => {
        var shortDate = formatDateString(value.end * 1000, "yyyyMMdd", false);
        if (chart_dates[shortDate] === -1) {
          chart_dates[shortDate] = index;
        } else if (chart_dates[shortDate] >= 0) {
          let idx = chart_dates[shortDate];
          if (array[idx].report_time < value.report_time) {
            chart_dates[shortDate] = index;
          }
        }
      });
      //去掉日期序列中开头的空白段
      var keysDate = Object.keys(chart_dates).reverse();
      for (i = 0; i < keysDate.length; i++) {
        if (chart_dates[keysDate[i]] >= 0) {
          break;
        } else if (i < keysDate.length - 29) { // 保留后 14 天，哪怕空白，为了图表不要太短
          delete chart_dates[keysDate[i]];
        }
      }
      //console.log(chart_dates);
  
    }
  }
  
  function initChartDates(reports, reportKey = '') {
    if (!reports) return;
  
    const max_chart_days = 30;
    let today;
  
    try {
      if (reportKey) {
        today = new Date(reportCalendarDate(reportKey) + 'T12:00:00');
        if (isNaN(today.getTime())) {
          throw new Error('Invalid date');
        }
      } else {
        today = new Date();
      }
  
      today.setHours(0, 0, 0, 0);
      let todayTS = today.getTime() / 1000;
      resetDates();
  
      // 生成日期序列，使用 M/D 格式（与 convertTrendDataToLegacy 返回的格式一致）
      for (let i = 0; i < max_chart_days; i++) {
        const date = new Date(todayTS * 1000);
        const month = date.getMonth() + 1;  // 不补零
        const day = date.getDate();         // 不补零
        const shortDate = `${month}/${day}`;
        chart_dates[shortDate] = -1;
        today.setDate(today.getDate() - 1);
      todayTS = today.getTime() / 1000;
      }

      // 如果 reports 是对象格式（新接口返回的格式），填充报告数据映射
      if (reports && typeof reports === 'object' && !Array.isArray(reports)) {
        Object.entries(reports).forEach(([dateKey, report]) => {
          // dateKey 应该是 "M/D" 格式（如 "11/7"）
          if (chart_dates[dateKey] !== undefined) {
            // 如果 chart_dates 中已有这个键，将其标记为有效
            if (chart_dates[dateKey] === -1) {
              chart_dates[dateKey] = dateKey;  // 存储实际的日期键，用于后续查找
            }
          }
        });
      }
    } catch (error) {
      console.error('Error initializing chart dates:', error);
    }
  }
  
  // 生命周期钩子：先拉取报告与用户信息，再渲染，避免打印页基本信息为空
  onMounted(async () => {
    try {
      isLoading.value = true
      const config = serverPdf
        ? updateDeviceType().catch(error => recordServerPdfError(error, 'config'))
        : Promise.resolve()
      await Promise.all([config, changeDate()])
      await nextTick()
      if (!serverPdfCohortProfileAwaited) await fetchReportInfo()
      window.addEventListener('resize', handleResize)
      reportResizeObserver = new ResizeObserver(handleResize)
      const content = document.getElementById('report-content')
      if (content) reportResizeObserver.observe(content)
      window.addEventListener('beforeprint', fitNativePrintPages)
      window.addEventListener('afterprint', restoreNativePrintPages)
    } finally {
      // isLoading.value = false
    }
  })
  // onMounted(() => {
  //   initCharts()
  //   updateCharts()
  //   window.addEventListener('resize', handleResize)
  // })
  
  onBeforeUnmount(() => {
    reportAbort.abort()
    chartUpdateDirty = false
    chartUpdateScheduled.value = false
    chartUpdatePromise = null
    chartUpdateGeneration++
    detailRequestGeneration++
    if (window.__wujiPrepareReportPdf === prepareReportPdfHook) {
      delete window.__wujiPrepareReportPdf
      delete window.__wujiReportPdfError
    }
    window.removeEventListener('resize', handleResize)
    reportResizeObserver?.disconnect()
    window.removeEventListener('beforeprint', fitNativePrintPages)
    window.removeEventListener('afterprint', restoreNativePrintPages)
    restoreNativePrintPages()
    finishNativeDownload()
    destroyCharts()
    setDisplayTimezone(0)
  })
  
  // 监听数��变化
  watch(() => data.value, updateCharts, {deep: true})
  watch(() => reports_data.value, updateCharts, {deep: true})
  
  // 添加一个函数来计算进度条宽度
  const getProgressWidth = (item) => {
    // 根据不同类型的数据返回不同的进度值
    if (item.label.includes('度')) {
      // 假设适宜温度范围是18-26度
      const temp = parseFloat(item.value)
      return `${Math.min(100, Math.max(0, ((temp - 18) / (26 - 18)) * 100))}%`
    }
    if (item.label.includes('湿度')) {
      // 假设适宜湿度范围是30-70%
      const humidity = parseFloat(item.value)
      return `${Math.min(100, (humidity / 100) * 100)}%`
    }
    if (item.label.includes('光照')) {
      // 直接使用百分比值
      const light = parseFloat(item.value)
      return `${Math.min(100, light)}%`
    }
    return '50%' // 默认值
  }

// 添加日期格式化函数
const formatChineseDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  if (currentLocale.value === 'en') return `${year}-${month}-${day}`;
  return `${year}年 ${month}月 ${day}日`;
}

// 添加日期格式化工具函数
const formatDateValue = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // 处理时间戳（��设是秒级时间戳）
    if (typeof dateValue === 'number') {
      const date = new Date(dateValue * 1000);
      return date.toISOString().split('T')[0];
    }
    
    // 处理 YYYY-MM-DD 格式的字符串
    if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }
    
    // 处理其他日期字符串
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.error('日期格式化错误:', error);
    return '';
  }
};

const fetchReportInfo = async () => {
  pendingProfiles.value++
  try { return await loadReportInfo() }
  catch (error) { recordServerPdfError(error); throw error }
  finally { pendingProfiles.value-- }
}
const loadReportInfo = async () => {
  if (props.cohortOrg) {
    cohortProfile.value = null
    profileError.value = ''
    try {
      if (!props.cohortSubjectId) throw new Error(t('cohort_profile_missing_context'))
      const profile = await getSubjectBasicInfo(props.cohortOrg, props.cohortSubjectId, reportCalendarDate(selectedReportDate.value))
      if (!isBasicInfoResponse(profile)) throw bulkPdfError('report')
      cohortProfile.value = profile
    } catch (error) {
      profileError.value = error.response?.data?.detail || t('cohort_profile_retry')
      throw error
    }
    return
  }
  if (!props.device || !selectedReportDate.value) return
  try {
    const axiosInstance = createAxiosInstance('计算服务器1')
    let formattedDate
    if (typeof selectedReportDate.value === 'string') {
      formattedDate = selectedReportDate.value.split('T')[0].split(' ')[0]
    } else if (selectedReportDate.value instanceof Date) {
      formattedDate = selectedReportDate.value.toISOString().split('T')[0]
    } else {
      const date = new Date(selectedReportDate.value)
      formattedDate = date.toISOString().split('T')[0]
    }
    const response = await axiosInstance.get(`report_info/${props.device}/${formattedDate}/`)
    const existing = { ...userinfo.value }
    if (response.data?.status === 'success' && response.data?.data) {
      const { basicInfo = {}, customFields: customFieldsData } = response.data.data
      // 合并接口数据与已有 userinfo，接口有值则覆盖，无值则保留已有
      userinfo.value = {
        username: basicInfo.name ?? existing.username ?? '',
        sex: basicInfo.gender ?? existing.sex ?? '',
        ethnicity: basicInfo.ethnicity ?? existing.ethnicity ?? '',
        birthday: basicInfo.birthDate != null ? formatDateValue(basicInfo.birthDate) : (existing.birthday ?? ''),
        age: basicInfo.age ?? existing.age ?? '',
        height: basicInfo.height != null ? basicInfo.height : existing.height,
        weight: basicInfo.weight != null ? basicInfo.weight : existing.weight,
        bmi: basicInfo.bmi ?? existing.bmi ?? '',
        customData: customFieldsData || existing.customData || {}
      }
      return
    }
  } catch (err) {
    console.warn('[打印页] fetchReportInfo 失败:', err)
  }
  // 接口无数据或失败时：保留已有 userinfo，并尝试用手机号拉取 /userinfo 补齐
  // 兜底链覆盖：路由 selections（OrgDashboard 来源 phone_number）、ReportList 来源 bound_phone、本地 userinfo
  const sel = selections.value?.userinfo || {}
  const phone =
    userinfo.value?.user ||
    userinfo.value?.phoneNumber ||
    userinfo.value?.phone_number ||
    sel.user ||
    sel.phoneNumber ||
    sel.phone_number ||
    sel.bound_phone ||
    selections.value?.bound_phone ||
    ''
  if (phone) {
    try {
      const profile = await fetchUserProfile(phone, '数据服务器')
      if (profile && typeof profile === 'object') {
        userinfo.value = {
          ...userinfo.value,
          username: userinfo.value?.username || profile.nickname || '',
          sex: userinfo.value?.sex || profile.sex || '',
          ethnicity: userinfo.value?.ethnicity || '',
          birthday: userinfo.value?.birthday || (profile.birthdate ? formatDateValue(profile.birthdate) : ''),
          height: userinfo.value?.height ?? profile.height ?? null,
          weight: userinfo.value?.weight ?? profile.weight ?? null,
          bmi: userinfo.value?.bmi || ''
        }
      }
    } catch (e) {
      console.warn('[打印页] /userinfo 补齐失败:', e)
    }
  }
  if (!userinfo.value?.username && !userinfo.value?.ethnicity && !userinfo.value?.birthday) {
    useDefaultUserInfo()
  }
}
// 添加日期格式化函数
const formatDate = (dateString) => {
  if (!dateString) return ''
  // 如果包含 %20，说明是完整的时间戳，需要截取日期部分
  if (dateString.includes('%20')) {
    return dateString.split('%20')[0]
  }
  // 如果已经是 YYYY-MM-DD 格式，直接返回
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString
  }
  // 其他情况，转换为 YYYY-MM-DD 格式
  const date = new Date(dateString)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// 添加获取问卷分数的方法
const fetchQuestionnaireScores = async () => {
  pendingQuestionnaires.value++
  try { return await loadQuestionnaireScores() } finally { pendingQuestionnaires.value-- }
}
const loadQuestionnaireScores = async () => {
  try {
    if (!props.device || !selectedReportDate.value) return
    
    const formattedDate = formatDate(selectedReportDate.value)
    const axios = createAxiosInstance('计算服务器1')
    
    // Independent scores settle separately; 404 means absent, other failures
    // still block server PDF export. Keep pendingQuestionnaires until BOTH settle.
    await Promise.all([
      (async () => {
        try {
          const response = await axios.get(`/questionnaire/${props.device}/${formattedDate}/ess`, serverPdf ? { signal: reportAbort.signal } : {})
          essScore.value = response.data.status === 'success' && response.data.data ? response.data.data.total_score : null
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error('获取 ESS 分数失败:', error)
            if (serverPdf) recordServerPdfError(error)
          }
          essScore.value = null
        }
      })(),
      (async () => {
        try {
          const response = await axios.get(`/questionnaire/${props.device}/${formattedDate}/psqi`, serverPdf ? { signal: reportAbort.signal } : {})
          psqiScore.value = response.data.status === 'success' && response.data.data ? response.data.data.total_score : null
        } catch (error) {
          if (error.response?.status !== 404) {
            console.error('获取 PSQI 分数失败:', error)
            if (serverPdf) recordServerPdfError(error)
          }
          psqiScore.value = null
        }
      })()
    ])
  } catch (error) {
    console.error('获取问卷分数失败:', error)
    recordServerPdfError(error)
    // 确保在出错时也重置分数
    essScore.value = null
    psqiScore.value = null
  }
}

// 抽取使用默认值的逻辑为单独的函数
const useDefaultUserInfo = () => {
  // 使用父组件传递的默认值
  const defaultUserInfo = props.selections?.userinfo;
  if (defaultUserInfo) {
    userinfo.value = { ...defaultUserInfo };
  } else {
    // 如果连默认值也没有，则清空所有字段
    userinfo.value = {
      username: '',
      sex: '',
      ethnicity: '',
      birthday: '',
      age: '',
      height: null,
      weight: null,
      bmi: '',
      customData: {}
    };
  }
}
// 计算年龄
const computedAge = computed(() => {
  if (!userinfo.value?.birthday) return '--'
  const birthDate = new Date(userinfo.value.birthday)
  const today = new Date()
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  
  return `${age}${t('age_unit')}`
})

// 计算 BMI
const computedBMI = computed(() => {
  if (!userinfo.value?.height || !userinfo.value?.weight) return '--'
  const height = userinfo.value.height / 100 // 转换为米
  const weight = userinfo.value.weight
  const bmi = (weight / (height * height)).toFixed(1)
  return bmi
})

// 基础信息配置
const baseInfoItems = computed(() => props.cohortOrg ? basicInfoItems(cohortProfile.value, t) : [
  {
    label: t('name'),
    value: userinfo.value?.username || '--'
  },
  {
    label: t('gender'),
    value: userinfo.value?.sex || '--'
  },
  {
    label: t('birthdate'),
    value: userinfo.value?.birthday || '--'
  },
  {
    label: t('age'),
    value: computedAge.value
  },
  {
    label: t('ethnicity'),
    value: userinfo.value?.ethnicity || '--'
  },
  {
    label: t('height'),
    value: userinfo.value?.height ? `${userinfo.value.height} cm` : '--'
  },
  {
    label: t('weight'),
    value: userinfo.value?.weight ? `${userinfo.value.weight} kg` : '--'
  },
  {
    label: 'BMI',
    value: computedBMI.value
  }
])

// 自定义字段处理
const customFields = computed(() => {
  const customData = props.cohortOrg ? {} : userinfo.value?.customData || {}
  
  // 直接返回处理后的对象,保持原有的数据结构
  return Object.entries(customData).reduce((acc, [key, fieldData]) => {
    // 如果数据格式不正确,返回默认值
    if (!fieldData || typeof fieldData !== 'object') {
      acc[key] = {
        value: '--',
        unit: ''
      }
      return acc
    }

    // 保持原有的值和单位
    acc[key] = {
      value: fieldData.value || '--',
      unit: fieldData.unit || ''
    }
    return acc
  }, {})
})
import { useRoute } from 'vue-router'
import { t, currentLocale } from '@/plugins/utils/i18n';

onMounted(() => {
  if (!serverPdf) return
  prepareReportPdfHook = async () => {
    try {
      return await prepareServerReportPdf({
        ready: () => reportReadiness.value.ready,
        error: () => serverPdfError.value,
        permitted: () => canResearchExport.value,
        prepare: ensureChartsRendered,
        fit: fitNativePrintPages,
        signal: reportAbort.signal
      })
    } catch (error) {
      const safe = bulkPdfError(bulkPdfErrorCode(error, 'render'))
      if (!reportAbort.signal.aborted) window.__wujiReportPdfError = safe.code
      throw safe
    }
  }
  window.__wujiPrepareReportPdf = prepareReportPdfHook
})
watch(serverPdfError, error => {
  if (serverPdf && error) window.__wujiReportPdfError = bulkPdfErrorCode(error, 'report')
}, { flush: 'sync' })

// 检查数据是否完全加载
// One authority for native-print and server-PDF readiness. Keep the complete
// dependency state here rather than rebuilding a divergent export predicate.
const reportReadiness = computed(() => {
  const gates = {
    isLoading: isLoading.value,
    profilePresent: !props.cohortOrg || !!cohortProfile.value,
    chartCount: Object.keys(chartInstances.value).length,
    pendingCharts: pendingCharts.value,
    pendingProfiles: pendingProfiles.value,
    pendingQuestionnaires: pendingQuestionnaires.value
  }
  const hasData = serverPdfConfigReady.value && !!data.value && Object.keys(data.value).length > 0 &&
    !!summmary.value && Object.keys(summmary.value).length > 0
  const dependenciesSettled = !isLoadingTrend.value && gates.pendingProfiles === 0 && gates.pendingQuestionnaires === 0
  const fullyLoaded = hasData && !gates.isLoading && !profileError.value && gates.profilePresent && gates.chartCount > 0 &&
    !isLoadingTrend.value && gates.pendingCharts === 0
  return { gates, fullyLoaded, dataReady: hasData && dependenciesSettled,
    ready: fullyLoaded && dependenciesSettled && !chartUpdateScheduled.value && gates.pendingCharts === 0 }
})
const isDataFullyLoaded = computed(() => reportReadiness.value.fullyLoaded)

// 确保图表在打印前已完全渲染
const ensureChartsRendered = async () => {
  // 确保图表实例存在
  if (Object.keys(chartInstances.value).length === 0) {
    await nextTick()
    initCharts()
    await nextTick()
  }
  
  // 触发所有图表 resize，确保它们正确渲染
  await nextTick()
  Object.values(chartInstances.value).forEach(chart => {
    if (chart) {
      try {
        // resize 已同步更新画布；重新灌入 getOption 会破坏重复打印时的坐标系。
        chart.resize()
      } catch (error) {
        console.warn('图表 resize 失败:', error)
      }
    }
  })
  
  // 等待图表渲染完成
  await new Promise(resolve => setTimeout(resolve, 500))
}

// Whole-page A4 fitting shared by native print and server-generated PDFs. Some legacy
// daily pages exceed A4 (especially English); never split a chart/footer across
// sheets. Measure under print CSS, restore after every dialog/capture.
const nativePrintSheets = new Map()
const restoreNativePrintPages = () => {
  nativePrintSheets.forEach(({ sheet, style }, page) => {
    sheet.replaceWith(page)
    if (style === null) page.removeAttribute('style')
    else page.setAttribute('style', style)
  })
  nativePrintSheets.clear()
}
const fitNativePrintPages = () => {
  restoreNativePrintPages()
  document.querySelectorAll('#report-content > .report-page').forEach(page => {
    if (getComputedStyle(page).display === 'none') return
    const box = page.getBoundingClientRect()
    const height = Math.max(box.height, page.scrollHeight)
    const sheetHeight = box.width * 297 / 210 - 2
    const scale = Math.min(1, sheetHeight / height)
    const sheet = document.createElement('div')
    sheet.className = 'native-print-sheet'
    Object.assign(sheet.style, { position: 'relative', width: `${box.width}px`, height: `${sheetHeight}px`,
      breakAfter: 'page', pageBreakAfter: 'always', breakInside: 'avoid' })
    nativePrintSheets.set(page, { sheet, style: page.getAttribute('style') })
    page.replaceWith(sheet)
    sheet.appendChild(page)
    // An absolutely positioned, transformed page cannot fragment internally.
    // Unlike CSS zoom, print fragmentation uses the fitted sheet's dimensions.
    page.style.setProperty('position', 'absolute', 'important')
    page.style.setProperty('break-after', 'auto', 'important')
    page.style.setProperty('page-break-after', 'auto', 'important')
    Object.assign(page.style, { top: '0', left: `${(box.width - box.width * scale) / 2}px`,
      transform: `scale(${scale})`, transformOrigin: 'top left' })
  })
  const last = [...nativePrintSheets.values()].at(-1)?.sheet
  if (last) { last.style.breakAfter = 'auto'; last.style.pageBreakAfter = 'auto' }
}

// 下载 PDF 状态
const isDownloading = ref(false)
let finishNativeDownload = () => {}

// 下载 PDF 函数 - 使用浏览器原生打印功能（直接在当前页面触发）
const downloadPDF = async () => {
  if (!canResearchExport.value) return
  if (isDownloading.value) return
  if (!isDataFullyLoaded.value) {
    ElMessage.warning(t('data_not_loaded_retry'))
    return
  }

  const downloadContext = `${selectedDevice.value}-${selectedReportDate.value}`
  isDownloading.value = true
  try {
    // 确保图表已完全渲染
    await ensureChartsRendered()
    
    // 等待图表渲染完成
    await new Promise(resolve => setTimeout(resolve, 500))
    if (reportAbort.signal.aborted || !canResearchExport.value || !isDataFullyLoaded.value ||
        downloadContext !== `${selectedDevice.value}-${selectedReportDate.value}`) {
      isDownloading.value = false
      return
    }
    
    // 在打印前，先将图表转换为图片，确保打印时图表能正确显示
    const element = document.getElementById('report-content')
    if (!element) {
      throw new Error('找不到报告内容')
    }
    
    // 将图表转换为图片（在打印时，ECharts可能无法正确渲染）
    const chartSelectors = [
      '[id^="stage_chart_"]', '[id^="art_chart_"]', '[id^="hr_chart_"]', '[id^="hrv_chart_"]',
      '[id^="rr_chart_"]', '[id^="ah_chart_"]', '[id^="spo2_chart_"]', '[id^="snore_chart_"]',
      '[id^="tst_chart_"]', '[id^="stage_comp_chart_"]', '[id^="snore_index_chart_"]',
      '[id^="sol_chart_"]', '[id^="waso_chart_"]', '[id^="se_chart_"]',
      '[id^="env_month_chart_"]', '[id^="deep_chart_"]', '[id^="sri_chart_"]',
      '[id^="sleep_age_chart_"]', '[id^="aging_pace_chart_"]', '[id^="recovery_chart_"]',
      '[id^="ahi_chart_"]', '[id^="rr_bl_chart_"]', '[id^="hr_bl_chart_"]', '[id^="hrv_bl_chart_"]'
    ]
    
    const chartImages = new Map()
    const originalDisplays = new Map()
    
    // 先将图表转换为图片
    chartSelectors.forEach(selector => {
      const chartEls = element.querySelectorAll(selector)
      chartEls.forEach(chartEl => {
        const chartId = chartEl.id
        
        for (const [key, chartInstance] of Object.entries(chartInstances.value)) {
          if (!chartInstance) continue
          
          try {
            const chartDom = chartInstance.getDom && chartInstance.getDom()
            if (chartDom && chartDom.id === chartId) {
              const container = chartEl.parentElement
              const containerWidth = container ? container.offsetWidth : 800
              
              const canvas = chartInstance.getRenderedCanvas({
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                width: containerWidth
              })
              
              if (canvas) {
                const imgData = canvas.toDataURL('image/png', 1.0)
                chartImages.set(chartId, imgData)
                
                // 创建图片元素
                const img = document.createElement('img')
                img.src = imgData
                img.style.width = '100%'
                img.style.height = 'auto'
                img.style.display = 'block'
                img.className = 'chart-print-img'
                img.setAttribute('data-chart-id', chartId)
                
                // 保存原始显示状态
                originalDisplays.set(chartId, chartEl.style.display)
                
                // 隐藏图表，显示图片
                chartEl.style.display = 'none'
                chartEl.parentElement.insertBefore(img, chartEl.nextSibling)
              }
              break
            }
          } catch (error) {
            console.warn(`无法渲染图表 ${chartId}:`, error)
          }
        }
      })
    })
    
    // 等待图片加载
    await new Promise(resolve => setTimeout(resolve, 300))
    
    // 在打印前，检测并优化页面布局，避免空白页
    const reportPages = element.querySelectorAll('.report-page')
    const hiddenPages = []
    const footerMargins = new Map()  // 存储修改过的页脚上边距，以便恢复
    
    // 等待一下，确保所有内容都已渲染
    await new Promise(resolve => setTimeout(resolve, 200))
    
    reportPages.forEach((page, index) => {
      const pageContent = page.querySelector('.page-content')
      const pageFooter = page.querySelector('.page-footer')
      const pageHeader = page.querySelector('.page-header')
      
      if (pageContent && pageFooter) {
        // 获取页面的实际渲染尺寸
        const pageRect = page.getBoundingClientRect()
        const contentRect = pageContent.getBoundingClientRect()
        const footerRect = pageFooter.getBoundingClientRect()
        const headerRect = pageHeader ? pageHeader.getBoundingClientRect() : null
        
        // 计算页面的可用高度（A4 高度减去 padding）
        // A4 高度 = 297mm ≈ 1123px (96dpi)，padding = 20mm * 2 = 40mm ≈ 151px
        // 可用高度 ≈ 972px
        const availableHeight = 972
        const headerHeight = headerRect ? headerRect.height : 0
        const footerHeight = footerRect.height
        const contentHeight = contentRect.height
        // 当前页脚上边距（从计算样式获取）
        const currentFooterMargin = parseInt(window.getComputedStyle(pageFooter).marginTop) || 0
        const totalUsedHeight = headerHeight + contentHeight + footerHeight + currentFooterMargin
        
        // 检查是否有可见的子元素
        const visibleChildren = Array.from(pageContent.children).filter(child => {
          const style = window.getComputedStyle(child)
          const rect = child.getBoundingClientRect()
          return style.display !== 'none' && 
                 style.visibility !== 'hidden' && 
                 style.opacity !== '0' &&
                 rect.width > 10 && 
                 rect.height > 10 &&
                 (child.scrollHeight > 30 || 
                  (child.textContent && child.textContent.trim().length > 10) ||
                  child.querySelector('img, canvas, svg, [id*="chart"]'))
        })
        
        const hasRealContent = visibleChildren.length > 0 && contentHeight > 50
        
        console.log(`页面 ${index + 1}: 内容高度=${contentHeight.toFixed(0)}px, 总高度=${totalUsedHeight.toFixed(0)}px, 可用高度=${availableHeight}px, 使用率=${(totalUsedHeight/availableHeight*100).toFixed(1)}%, 可见子元素=${visibleChildren.length}`)
        
        // 如果页面真的没有内容，隐藏该页面
        if (!hasRealContent && contentHeight < 50) {
          page.classList.add('print-hidden-page')
          page.style.display = 'none'
          hiddenPages.push(page)
          console.log(`✅ 隐藏空页面 ${index + 1}`)
        } else if (hasRealContent) {
          // 如果页面有内容，但使用率低于70%，减小页脚上边距，避免产生空白
          if (totalUsedHeight < availableHeight * 0.7) {
            // 内容不足一页，减小页脚上边距到最小（2mm）
            const newMargin = '2mm'
            footerMargins.set(pageFooter, currentFooterMargin)
            pageFooter.style.marginTop = newMargin
            console.log(`页面 ${index + 1}: 内容不足（使用率 ${(totalUsedHeight/availableHeight*100).toFixed(1)}%），减小页脚上边距到 ${newMargin}`)
          } else if (totalUsedHeight < availableHeight * 0.85) {
            // 内容接近一页但还有空间，使用较小的页脚上边距（3mm）
            const newMargin = '3mm'
            footerMargins.set(pageFooter, currentFooterMargin)
            pageFooter.style.marginTop = newMargin
            console.log(`页面 ${index + 1}: 内容较少（使用率 ${(totalUsedHeight/availableHeight*100).toFixed(1)}%），减小页脚上边距到 ${newMargin}`)
        }
          // 如果使用率超过85%，使用默认的5mm上边距
        }
      } else if (!pageContent) {
        // 如果没有 page-content，检查是否有其他内容
        const hasAnyContent = page.children.length > 0 && 
          Array.from(page.children).some(child => {
            const style = window.getComputedStyle(child)
            return style.display !== 'none' && child.textContent && child.textContent.trim().length > 0
          })
        
        if (!hasAnyContent) {
          page.classList.add('print-hidden-page')
          page.style.display = 'none'
          hiddenPages.push(page)
          console.log(`✅ 隐藏空页面 ${index + 1}（无 page-content 且无其他内容）`)
        }
      }
    })
    
    console.log(`总共 ${reportPages.length} 个页面，隐藏了 ${hiddenPages.length} 个空页面，调整了 ${footerMargins.size} 个页脚上边距`)
    
    // 添加打印事件监听，打印完成后恢复图表和页面显示
    const handleAfterPrint = () => {
      // 恢复图表显示
      chartImages.forEach((imgData, chartId) => {
        const chartEl = document.getElementById(chartId)
        const imgEl = document.querySelector(`.chart-print-img[data-chart-id="${chartId}"]`)
        
        if (chartEl) {
          chartEl.style.display = originalDisplays.get(chartId) || ''
        }
        if (imgEl) {
          imgEl.remove()
        }
      })
      
      // 恢复隐藏的页面和页脚样式
      hiddenPages.forEach(page => {
        page.classList.remove('print-hidden-page')
        page.style.display = ''
      })
      
      // 恢复所有修改过的页脚上边距
      footerMargins.forEach((originalMargin, pageFooter) => {
        pageFooter.style.marginTop = originalMargin ? `${originalMargin}px` : ''
      })
      footerMargins.clear()
      
      window.removeEventListener('afterprint', handleAfterPrint)
      finishNativeDownload = () => {}
      isDownloading.value = false
    }
    
    finishNativeDownload = handleAfterPrint
    window.addEventListener('afterprint', handleAfterPrint)
    
    // 触发打印
    if (!canResearchExport.value) { handleAfterPrint(); return }
    window.print()
    
    ElMessage.info(currentLocale.value === 'en' ? 'In the print dialog, choose "Save as PDF" and click Save.' : '请在弹出的打印对话框中选择"另存为PDF"，然后点击"保存"')
  } catch (error) {
    finishNativeDownload()
    console.error('打印失败:', error)
    ElMessage.error(t('print_failed_retry'))
    isDownloading.value = false
  }
}
  </script>
  
<style scoped>
/* 添加新的样式 */
.report-container {
  width: 100%;
  max-width: 210mm;  /* A4 宽度限制 */
  margin: 0 auto;
  box-sizing: border-box;
  overflow-x: hidden;
}

.report-page {
  width: 210mm;  /* A4 宽度 */
  max-width: 210mm;  /* 确保不超过A4宽度 */
  margin: 0 auto;
  padding: 20mm 15mm;  /* 上下左右留白 */
  background: white;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  position: relative;
  box-sizing: border-box;
  page-break-after: auto;
  page-break-inside: avoid;
  overflow-x: hidden;
  overflow-y: visible;
  /* 预览时使用 flexbox 布局，但打印时会覆盖为 block */
  display: flex;
  flex-direction: column;
  /* 不设置固定的 min-height，让内容决定页面高度 */
  min-height: auto;
}

.page-header {
  @apply bg-white mb-6;
  position: relative;
  margin: -20mm -15mm 6mm -15mm; /* 抵消父容器的 padding */
  padding: 20mm 15mm 0 15mm;
}

.page-content {
  /* 内容区域自动扩展，不设置固定高度 */
  flex: 1 1 auto;  /* 占据剩余空间，但可以收缩（预览时） */
  height: auto;  /* 允许内容自动扩展 */
  min-height: 0;  /* 允许收缩到内容高度 */
  overflow: visible;  /* 允许内容溢出，让截图捕获 */
  /* 打印时会覆盖这些 flex 属性 */
}

.content-section {
  @apply bg-white rounded-lg shadow-sm p-3 border border-gray-100;
}

.page-footer {
    text-align: center;
  padding: 15mm 0 0 0;  /* 顶部间距 */
  /* 预览时使用 margin-top: auto 将页脚推到底部 */
  margin-top: auto;
  flex-shrink: 0;  /* 页脚不收缩 */
  /* 打印时会覆盖为 margin-top: 15mm，紧贴内容 */
}

@media screen and (max-width: 767px) {
  .report-page { width: 100%; padding: 20px 12px; }
  .page-header { margin: -20px -12px 24px; padding: 20px 12px 0; }
}

/* 打印样式优化 */
@media print {
  :global(.el-message) { display: none !important; }
  /* 全局样式：隐藏浏览器 UI 和不需要的元素 */
  @page {
    size: A4;
    margin: 0;
  }

  /* 确保 body 和 html 在打印时没有边距和滚动条 */
  body {
    margin: 0 !important;
    padding: 0 !important;
    width: 210mm;
    height: 100%;
    overflow: hidden;
    background: white !important;
  }

  html {
    margin: 0 !important;
    padding: 0 !important;
    width: 210mm;
    height: 100%;
    overflow: hidden;
  }

  /* 隐藏所有不需要的元素 */
  .no-print,
  nav,
  header:not(.page-header),
  footer:not(.page-footer),
  button,
  .fixed,
  .loading,
  [class*="loading"],
  iframe,
  script,
  style:not([data-print-style]) {
    display: none !important;
    visibility: hidden !important;
  }

  /* 隐藏 loading 遮罩 */
  div[v-if*="isLoading"],
  .animate-spin {
    display: none !important;
  }

  /* 报告容器样式 */
  .report-container {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    background: white !important;
    box-shadow: none !important;
  }

  /* 报告页面样式 */
  .report-page {
    margin: 0 auto !important;
    padding: 20mm 15mm !important;
    width: 210mm !important;
    /* 移除固定的 min-height，让内容决定页面高度，避免空白页 */
    height: auto !important;
    min-height: auto !important;
    box-shadow: none !important;
    /* 移除 break-inside: avoid，允许页面内容在需要时自然分割，避免产生空白页 */
    break-inside: auto !important;
    page-break-inside: auto !important;
    background: white !important;
    position: relative !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    display: block !important;  /* 改为 block，不使用 flexbox，避免空白 */
  }
  
  /* 隐藏的页面不显示，也不分页 */
  .report-page.print-hidden-page,
  .report-page[style*="display: none"] {
    display: none !important;
    page-break-after: auto !important;
    break-after: auto !important;
    visibility: hidden !important;
  }
  
  /* 只在有内容的页面后分页，避免空白页 */
  /* 第一页后不强制分页，让浏览器根据内容自然分页，避免产生空白页 */
  .report-page:first-child:not(.print-hidden-page) {
    break-after: page !important;
    page-break-after: always !important;
  }
  
  /* 其他非最后一个页面也分页 */
  .report-page:not(:first-child):not(:last-child):not(.print-hidden-page) {
    break-after: page !important;
    page-break-after: always !important;
  }
  
  /* 最后一个页面不分页，避免空白页 */
  .report-page:last-child {
    break-after: auto !important;
    page-break-after: auto !important;
  }
  
  /* 确保页面内容完整显示，不被截断 */
  .report-page * {
    max-width: 100% !important;
    box-sizing: border-box !important;
  }

  /* 页眉样式 */
  .page-header {
    margin: -20mm -15mm 6mm -15mm !important;
    padding: 0 15mm 0 15mm !important;
    background: white !important;
  }

  /* 页眉顶部间距调整 */
  .page-header > div:first-child {
    padding-top: 15mm !important;
  }

  /* 页脚样式 - 使用相对定位，紧贴内容，不产生空白 */
  .page-footer {
    position: relative !important;
    margin-top: 5mm !important;  /* 进一步减少上边距，避免产生空白 */
    margin-bottom: 0 !important;
    padding: 0 !important;
    text-align: center !important;
    /* 紧贴内容，不推到底部，避免产生空白 */
    flex-shrink: 0 !important;
    page-break-after: avoid !important;  /* 避免页脚被单独分到下一页 */
  }

  /* 确保每个部分在打印时保持在同一页 */
  .content-section {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* 确保图表容器在打印时正确显示 */
  .print-chart-container {
    min-height: 140px !important;
    height: 140px !important;
    overflow: visible !important;
  }
  
  .chart-container,
  [id^="stage_chart_"],
  [id^="art_chart_"],
  [id^="env_chart_"],
  [id^="hrv_chart_"],
  [id^="rr_chart_"],
  [id^="ah_chart_"],
  [id^="hr_chart_"],
  [id^="rr_bl_chart_"],
  [id^="hr_bl_chart_"],
  [id^="hrv_bl_chart_"],
  [id^="ahi_chart_"],
  [id^="spo2_chart_"],
  [id^="snore_chart_"],
  [id^="tst_chart_"],
  [id^="stage_comp_chart_"],
  [id^="snore_index_chart_"],
  [id^="sol_chart_"],
  [id^="waso_chart_"],
  [id^="se_chart_"],
  [id^="env_month_chart_"],
  [id^="deep_chart_"],
  [id^="sri_chart_"],
  [id^="sleep_age_chart_"],
  [id^="aging_pace_chart_"],
  [id^="recovery_chart_"] {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* 确保颜色在打印时正确显示 */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* 隐藏滚动条 */
  * {
    overflow: visible !important;
  }

  /* 确保文本和背景色正确显示 */
  .bg-white {
    background-color: white !important;
  }

  .bg-gray-50 {
    background-color: #f9fafb !important;
  }

  .bg-blue-600 {
    background-color: #2563eb !important;
  }

  .text-blue-600 {
    color: #2563eb !important;
  }

  /* 确保边框和阴影在打印时正确显示 */
  .border,
  .border-gray-100 {
    border-color: #f3f4f6 !important;
  }

  /* 报告页面样式 - 移除固定的 min-height，让内容决定高度，避免空白页 */
  .report-page {
    width: 210mm !important;
    max-width: 210mm !important;
    box-sizing: border-box !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    /* 移除 min-height，让内容自然填充，避免空白页 */
    min-height: auto !important;
    height: auto !important;
    /* 使用 block 布局，不使用 flexbox，避免产生空白 */
    display: block !important;
  }
  
  /* 除了最后一个页面，其他页面后都分页 */
  /* 隐藏的页面不显示，也不分页 */
  .report-page.print-hidden-page,
  .report-page[style*="display: none"] {
    display: none !important;
    page-break-after: auto !important;
    break-after: auto !important;
    visibility: hidden !important;
  }
  
  /* 只在有内容的页面后分页，避免空白页 */
  /* 第一页也保持逻辑页边界，确保第二页从新页开始。 */
  .report-page:first-child:not(.print-hidden-page) {
    page-break-after: always !important;
    break-after: page !important;
  }
  
  /* 其他非最后一个页面也分页 */
  .report-page:not(:first-child):not(:last-child):not(.print-hidden-page) {
    page-break-after: always !important;
    break-after: page !important;
  }
  
  /* 最后一个页面不需要分页，避免空白页 */
  .report-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }
  
  /* 页脚紧贴内容，不使用绝对定位，不产生空白 */
  .page-footer {
    position: relative !important;
    margin-top: 5mm !important;  /* 进一步减少上边距，避免产生空白 */
    margin-bottom: 0 !important;
    padding: 0 !important;
    text-align: center !important;
    /* 紧贴内容，不推到底部，避免产生空白 */
    page-break-after: avoid !important;  /* 避免页脚被单独分到下一页 */
  }
  
  /* 允许 page-content 在需要时分割，避免产生空白页 */
  .page-content {
    /* 移除 page-break-inside: avoid，允许内容在页面边界自然分割 */
    page-break-inside: auto !important;
    break-inside: auto !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    /* 移除 flex 相关属性，使用普通流布局 */
    flex: none !important;
    min-height: auto !important;
  }
  
  /* 但内容区块（content-section）尽量保持完整，不被分割 */
  .content-section {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  .monthly-print-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }

  /* 睡眠结构布局：PDF中强制2x2网格 */
  .print-sleep-structure {
    width: 100% !important;
    max-width: 100% !important;
    flex-direction: column !important;
  }
  
  .print-sleep-phases {
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .print-grid-2x2 {
    grid-template-columns: repeat(2, 1fr) !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  
  /* 睡眠期生理指标图表区域 - 打印时强制2列布局（覆盖 Tailwind 的 grid-cols-1） */
  .print-physiological-charts {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    display: grid !important;
    gap: 12px !important;
  }
  
  /* 确保所有容器不溢出 */
  .content-section,
  .bg-white.rounded-lg {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 导出图片随打印列宽变宽时，保持140px高度，避免时间刻度被容器裁掉。 */
  .print-chart-container :deep(.chart-print-img),
  .trend-chart-container :deep(.chart-print-img) {
    height: 100% !important;
  }

  /* 图表容器宽度限制 */
  .print-chart-container,
  .trend-chart-container {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }
  
  /* Grid布局宽度限制 */
  .grid {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 覆盖 Tailwind 的 grid-cols-1，在打印时对生理指标图表强制使用2列 */
  .grid.grid-cols-1.print-physiological-charts,
  .grid.md\:grid-cols-2.print-physiological-charts {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  
  /* Flex布局宽度限制 */
  .flex {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  
  /* 如果页面内容很少，不要创建空白页 */
  .report-page:empty {
    display: none !important;
  }
  
  /* 检查页面内容是否为空（使用更兼容的方式） */
  .report-page .page-content:empty {
    display: none !important;
  }
  
  /* 如果页面只有页眉和页脚，没有实际内容，隐藏整个页面 */
  .report-page:has(.page-content:empty),
  .report-page:has(.page-content:not(:has(*))) {
    display: none !important;
  }
}
</style>

<!-- 全局打印样式（非 scoped，用于隐藏浏览器 UI） -->
<style>
@media print {
  /* 隐藏浏览器地址栏和工具栏（通过设置页面大小和边距） */
  @page {
    size: A4 portrait;
    margin: 0;
  }

  /* 确保整个页面在打印时没有多余的空白 */
  body {
    margin: 0 !important;
    padding: 0 !important;
    width: 210mm !important;
    min-height: auto !important;
    background: white !important;
    overflow: visible !important;
  }

  html {
    margin: 0 !important;
    padding: 0 !important;
    width: 210mm !important;
    height: auto !important;
    overflow: visible !important;
    background: white !important;
  }

  /* 隐藏所有可能显示浏览器 UI 的元素 */
  ::-webkit-scrollbar {
    display: none !important;
  }

  /* 确保打印时页面内容居中且没有多余空白 */
  #app,
  .app {
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    background: white !important;
  }
  
  /* 打印时显示图表图片，隐藏原始图表容器 */
  .chart-print-img {
    display: block !important;
    width: 100% !important;
    height: auto !important;
    max-width: 100% !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }
  
  /* 打印时隐藏原始图表容器（如果旁边有打印图片） */
  /* 注意：:has() 选择器在某些浏览器中可能不支持，所以我们在 JavaScript 中直接设置 display: none */
  /* 这里只作为备用，JavaScript 中已经处理了 */
}
</style>